import { MENU_ICONS } from './menu-icons';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Renderer2,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, isActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TooltipModule } from 'primeng/tooltip';
import { filter, map, startWith } from 'rxjs/operators';

import { AuthService } from '../../features/auth/auth.service';
import { AuthActions } from '../../features/auth/state/action-typs';
import { ResetDashboardStateAction } from '../../features/dashboard/state/action-types';
import { MenuService } from '../../features/nav/menu.service';
import { ThemeToggleComponent } from '../../features/nav/theme-toggle.component';
import { UiToggleComponent } from '../../features/nav/ui-toggle.component';
import { AiAssistantComponent } from '../../shared/components/ai-assistant/ai-assistant.component';
import { ConfirmDialogComponent } from '../../shared/components/competitions/dialogs/confirm-dialog/confirm-dialog.component';
import { HelpPanelService } from '../../shared/components/help-panel/help-panel.service';
import { UserDetailService } from '../../shared/services/user-detail.service';
import { GetLocalStorageValues } from '../../shared/util/local-storage';
import { AppState } from '../../state/app.reducer';
import { Menu } from '../../types';

interface AppNavigationBridge {
  postMessage(message: 'home'): void;
}

type AppWebViewWindow = Window & {
  AppNavigation?: AppNavigationBridge;
};

interface NavGroup {
  label: string;
  items: Menu[];
}


const FALLBACK_LOGO = 'assets/img/activitypro-logo-dark.png';
const COLLAPSED_KEY = 'app-nu-sidebar-collapsed';
const ACCENT_KEY = 'app-accent';

interface AccentPreset {
  id: string;
  label: string;
  accent: string;
  dark: string;
  accent2: string;
  accent3: string;
  soft: string;
}

/** Daylight accent presets. Emerald is the default and matches the tokens in _tokens.scss. */
const ACCENT_PRESETS: readonly AccentPreset[] = [
  { id: 'emerald', label: 'Emerald', accent: '#047857', dark: '#065F46', accent2: '#10B981', accent3: '#8EE0C2', soft: '#E7F8F1' },
  { id: 'teal', label: 'Teal', accent: '#0B7A6E', dark: '#08655B', accent2: '#0E9C8C', accent3: '#8FDCCF', soft: '#E3F6F3' },
  { id: 'indigo', label: 'Indigo', accent: '#4652D6', dark: '#3A45B8', accent2: '#5B67E8', accent3: '#B3B9F6', soft: '#ECEEFD' },
  { id: 'azure', label: 'Azure', accent: '#0378BC', dark: '#02679F', accent2: '#1499DA', accent3: '#8FD0F2', soft: '#E6F5FC' },
];
/** Inline :root custom properties written by applyAccent. */
const ACCENT_VARS: readonly string[] = [
  '--nu-accent', '--nu-accent-dk', '--nu-accent-2', '--nu-accent-3', '--nu-accent-soft', '--nu-teal', '--nu-deep',
];
/** Page-level PrimeNG surfaces that own their Escape handling (the shell must not also react). */
const OWN_ESCAPE_SELECTOR = '.p-dialog, .p-dialog-mask, .p-overlay, .p-popover, .p-select-overlay, .p-datepicker-panel';

@Component({
  selector: 'app-new-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    TooltipModule,
    UiToggleComponent,
    ThemeToggleComponent,
    AiAssistantComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './new-shell.component.html',
  styleUrls: ['./new-shell.component.scss'],
  host: {
    '[class.has-banner]': 'hasBanner()',
    '[class.is-collapsed]': 'iconOnly()',
    '[class.drawer-open]': 'drawerOpen()',
    '[class.assistant-open]': 'isAiAssistantOpen()',
    '[style.--nu-help]': 'helpOffset()',
    '(document:keydown.escape)': 'onEscape($event)',
  },
})
export class NewShellComponent {
  /** True while the fixed impersonation / super-user banner is shown; shifts the shell below it. */
  readonly hasBanner = input(false);

  private readonly router = inject(Router);
  private readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly renderer = inject(Renderer2);
  private readonly doc = inject(DOCUMENT);
  private readonly menuService = inject(MenuService);
  private readonly authService = inject(AuthService);
  private readonly helpPanelService = inject(HelpPanelService);
  private readonly userDetailService = inject(UserDetailService);
  private readonly ls = new GetLocalStorageValues();
  private resolvingLogo = false;
  private clubNameTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly hamburgerBtn = viewChild<ElementRef<HTMLButtonElement>>('hamburgerBtn');
  private readonly accountWrap = viewChild<ElementRef<HTMLElement>>('accountWrap');
  private readonly accountBtn = viewChild<ElementRef<HTMLButtonElement>>('accountBtn');
  private readonly drawerCloseBtn = viewChild<ElementRef<HTMLButtonElement>>('drawerCloseBtn');
  private readonly askAiBtn = viewChild<ElementRef<HTMLButtonElement>>('askAiBtn');
  private readonly mainEl = viewChild<ElementRef<HTMLElement>>('mainEl');
  private readonly accentWrap = viewChild<ElementRef<HTMLElement>>('accentWrap');
  private readonly accentBtn = viewChild<ElementRef<HTMLButtonElement>>('accentBtn');

  /**
   * Accent colour picker (settings button + popover). Intentionally hidden for now: the product
   * decision is to ship the Emerald brand accent only. The picker code, persistence ('app-accent')
   * and styles stay in place; flip this to true to show the trigger again.
   */
  protected readonly showAccentPicker = false;
  protected readonly accentPresets = ACCENT_PRESETS;
  protected readonly accentId = signal(ACCENT_PRESETS[0].id);
  protected readonly accentOpen = signal(false);

  /** Dashboard is a prefix of the super-user dashboard route, so it must match exactly. */
  protected readonly dashboardRoute = 'main/dashboard';

  // Menu
  protected readonly menus = toSignal(this.menuService.menus$, { initialValue: [] as Menu[] });
  protected readonly groups = computed<NavGroup[]>(() => {
    const out: NavGroup[] = [];
    for (const menu of this.menus()) {
      const label = menu.category ?? '';
      const last = out[out.length - 1];
      if (last && last.label === label) {
        last.items.push(menu);
      } else {
        out.push({ label, items: [menu] });
      }
    }
    return out;
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** Key of the submenu parent that contains the active route (or ''). */
  private readonly activeParentKey = computed(() => {
    this.currentUrl();
    const parent = this.menus().find(m =>
      m.subMenu?.some(sub => isActive('/' + sub.route, this.router, {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })()),
    );
    return parent ? this.menuKey(parent) : '';
  });

  /**
   * Accordion: one open parent. Resets to the active parent on every navigation (the source also
   * tracks the URL, so it re-fires even when the active parent is unchanged); user can still toggle.
   */
  protected readonly expandedKey = linkedSignal<{ url: string; key: string }, string>({
    source: () => ({ url: this.currentUrl(), key: this.activeParentKey() }),
    computation: ({ key }) => key,
  });

  // User / club
  protected readonly parentClubLogo = signal(FALLBACK_LOGO);
  protected readonly clubName = signal('');
  protected readonly userName = signal('');
  protected readonly userEmail = signal('');
  protected readonly isSuperUserLoggedIn = signal(localStorage.getItem('superUser') === 'true');
  protected readonly isAdminAppWebView = signal(this.detectAdminAppWebView());

  protected readonly initial = computed(() => {
    const name = this.userName();
    return name ? name[0].toUpperCase() : '?';
  });

  protected readonly maskedEmail = computed(() => {
    const email = this.userEmail();
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    return `${local.slice(0, 3)}***@${domain}`;
  });

  // Shell state
  protected readonly collapsed = signal(this.loadCollapsed());
  /** Off-canvas drawer breakpoint (hamburger shown); keep in sync with the 860px rules in the SCSS. */
  protected readonly isMobile = this.mediaSignal('(max-width: 860px)');
  private readonly isNarrow = this.mediaSignal('(max-width: 1024px)');
  /** Must match the assistant's overlay breakpoint: at or below it the panel is a modal overlay. */
  private readonly isAssistantOverlay = this.mediaSignal('(max-width: 1100px)');
  protected readonly drawerOpen = signal(false);
  protected readonly accountOpen = signal(false);
  protected readonly showLogoutConfirm = signal(false);
  protected readonly isAiAssistantOpen = signal(false);

  /** Open off-canvas drawer: modal, so header + main are inert and focus stays in the drawer. */
  protected readonly drawerModal = computed(() => this.isMobile() && this.drawerOpen());

  /** Icon-only sidebar: desktop collapsed mode (the mobile drawer always shows labels). */
  protected readonly iconOnly = computed(() => this.collapsed() && !this.isMobile());

  /**
   * Passive only: the shell never opens the help panel, but reserves room if a page does.
   * Space reserved for the (fixed) help panel; it overlays on narrow screens like classic.
   */
  protected readonly helpOffset = computed(() =>
    this.helpPanelService.isOpen() && !this.isNarrow() ? `${this.helpPanelService.panelWidth()}px` : '0px',
  );

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.clubNameTimer));
    this.syncAccent();
    this.watchThemeForAccent();

    this.userDetailService.loggedOnUserDetails$.pipe(takeUntilDestroyed()).subscribe((data: any) => {
      this.userName.set(data?.name ?? '');
      this.userEmail.set(data?.emailId ?? '');
      if (data) {
        this.parentClubLogo.set(data?.parentClubLogo || FALLBACK_LOGO);
        if (!data?.parentClubLogo) {
          this.resolveClubLogo(data);
        }
        this.clubName.set(localStorage.getItem('clubName') || data?.name || '');
        // Re-read club name after a short delay (login flow may set it after this fires)
        clearTimeout(this.clubNameTimer);
        this.clubNameTimer = setTimeout(() => {
          const clubName = localStorage.getItem('clubName');
          if (clubName) this.clubName.set(clubName);
        }, 500);
      }
      if (data?.roleType) {
        // Classic navbar / side-nav drive menu permissions from here; they are not mounted in the new UI.
        this.menuService.setRoleType(data.roleType, data?.isAllowForBooking ?? false);
      }
    });

    this.userDetailService.isSuperUserLoggedIn.pipe(takeUntilDestroyed()).subscribe(isSuper => {
      this.isSuperUserLoggedIn.set(isSuper);
    });

    // Close the drawer after navigating.
    effect(() => {
      this.currentUrl();
      untracked(() => this.drawerOpen.set(false));
    });

    // Move focus into the drawer when it opens (the aside loses `inert` during this render pass).
    effect(() => {
      if (this.drawerOpen()) {
        afterNextRender(() => this.drawerCloseBtn()?.nativeElement.focus(), { injector: this.injector });
      }
    });

    // Outside-click listener only exists while the account menu is open, so idle clicks
    // do not run handlers / dirty this OnPush shell.
    effect(onCleanup => {
      if (!this.accountOpen()) return;
      const unlisten = this.renderer.listen('document', 'click', (event: Event) => this.onDocumentClick(event));
      onCleanup(unlisten);
    });

    // Move focus into the accent popover when it opens.
    effect(() => {
      if (this.accentOpen()) {
        afterNextRender(
          () => this.accentWrap()?.nativeElement.querySelector<HTMLButtonElement>('.nu-sw-btn.is-on, .nu-sw-btn')?.focus(),
          { injector: this.injector },
        );
      }
    });

    // Outside-click listener only while the accent popover is open.
    effect(onCleanup => {
      if (!this.accentOpen()) return;
      const unlisten = this.renderer.listen('document', 'click', (event: Event) => {
        const wrap = this.accentWrap()?.nativeElement;
        if (wrap && !wrap.contains(event.target as Node)) this.accentOpen.set(false);
      });
      onCleanup(unlisten);
    });

    // Leaving mobile width closes the drawer.
    effect(() => {
      if (!this.isMobile()) {
        untracked(() => this.drawerOpen.set(false));
      }
    });
  }

  protected menuKey(menu: Menu): string {
    return `${menu.category ?? ''}|${menu.name}`;
  }

  protected iconFor(name: string): string {
    return MENU_ICONS[name] ?? '';
  }

  protected subId(menu: Menu): string {
    return 'nu-sub-' + this.menuKey(menu).replace(/[^a-zA-Z0-9]+/g, '-');
  }

  protected isActiveParent(menu: Menu): boolean {
    return this.activeParentKey() === this.menuKey(menu);
  }

  protected isOpenParent(menu: Menu): boolean {
    return this.expandedKey() === this.menuKey(menu) && !this.iconOnly();
  }

  protected toggleParent(menu: Menu): void {
    const key = this.menuKey(menu);
    if (this.iconOnly()) {
      // Icon-only rail has no room for the accordion: expand the rail and open the group.
      this.setCollapsed(false);
      this.expandedKey.set(key);
      return;
    }
    this.expandedKey.set(this.expandedKey() === key ? '' : key);
  }

  protected toggleCollapsed(): void {
    this.setCollapsed(!this.collapsed());
  }

  private setCollapsed(value: boolean): void {
    this.collapsed.set(value);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(value));
    } catch {
      // Storage can be unavailable in restricted contexts; keep in-memory state.
    }
  }

  protected openDrawer(): void {
    this.drawerOpen.set(true);
  }

  protected closeDrawer(restoreFocus = false): void {
    this.drawerOpen.set(false);
    if (restoreFocus) {
      // The header is inert while the drawer is open, so focus only after the next render lifts it.
      afterNextRender(() => this.hamburgerBtn()?.nativeElement.focus(), { injector: this.injector });
    }
  }

  protected onEscape(event: Event): void {
    if (event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : null;
    // A page dialog / overlay (e.g. the dashboard quick-links editor) handles its own Escape.
    if (target?.closest(OWN_ESCAPE_SELECTOR)) return;

    if (this.accentOpen()) {
      this.closeAccentPicker(true);
      return;
    }
    if (this.accountOpen()) {
      this.accountOpen.set(false);
      this.accountBtn()?.nativeElement.focus();
      return;
    }
    // Docked (non-modal) assistant closes only from inside the panel (its own handler); the shell
    // handles Escape for the modal overlay, where focus is trapped in the panel.
    if (this.isAiAssistantOpen() && (this.isAssistantOverlay() || target?.closest('app-ai-assistant'))) {
      this.closeAiAssistant();
      return;
    }
    if (this.drawerOpen()) {
      this.closeDrawer(true);
    }
  }

  protected onDocumentClick(event: Event): void {
    if (!this.accountOpen()) return;
    const wrap = this.accountWrap()?.nativeElement;
    if (wrap && !wrap.contains(event.target as Node)) {
      this.accountOpen.set(false);
    }
  }

  /** Skip link: move focus to the main landmark without touching the router URL. */
  protected skipToMain(event: Event): void {
    event.preventDefault();
    const main = this.mainEl()?.nativeElement;
    main?.focus();
    main?.scrollIntoView?.({ block: 'start' });
  }

  protected toggleAccentPicker(): void {
    this.accentOpen.update(v => !v);
  }

  protected closeAccentPicker(restoreFocus = false): void {
    this.accentOpen.set(false);
    if (restoreFocus) {
      this.accentBtn()?.nativeElement.focus();
    }
  }

  protected selectAccent(id: string): void {
    const preset = ACCENT_PRESETS.find(p => p.id === id);
    if (!preset) return;
    this.applyAccent(preset);
    try {
      localStorage.setItem(ACCENT_KEY, preset.id);
    } catch {
      // Storage can be unavailable in restricted contexts; the choice still applies for this session.
    }
  }

  /** Presets are tuned for the light theme only; dark keeps the tokens from _tokens.scss. */
  private isDarkTheme(): boolean {
    return this.doc.documentElement.getAttribute('data-theme') === 'dark';
  }

  private applyAccent(preset: AccentPreset): void {
    this.accentId.set(preset.id);
    if (this.isDarkTheme()) {
      // Inline :root vars would beat the dark tokens (light accents on dark surfaces fail contrast).
      this.clearAccent();
      return;
    }
    const style = this.doc.documentElement.style;
    style.setProperty('--nu-accent', preset.accent);
    style.setProperty('--nu-accent-dk', preset.dark);
    style.setProperty('--nu-accent-2', preset.accent2);
    style.setProperty('--nu-accent-3', preset.accent3);
    style.setProperty('--nu-accent-soft', preset.soft);
    // Legacy new-UI brand tokens follow the accent (dialogs, charts, etc. still read these).
    style.setProperty('--nu-teal', preset.accent);
    style.setProperty('--nu-deep', preset.dark);
  }

  /** Removes every inline accent var this shell may have set (falls back to the stylesheet tokens). */
  private clearAccent(): void {
    const style = this.doc.documentElement.style;
    ACCENT_VARS.forEach(name => style.removeProperty(name));
  }

  /**
   * Single place that decides whether an accent is applied: only while the picker is shown, the
   * new UI is active and the theme is light. Otherwise any inline accent vars are removed.
   */
  private syncAccent(): void {
    const root = this.doc.documentElement;
    if (!this.showAccentPicker || root.getAttribute('data-ui') !== 'new' || this.isDarkTheme()) {
      this.clearAccent();
      return;
    }
    try {
      const saved = localStorage.getItem(ACCENT_KEY);
      const preset = ACCENT_PRESETS.find(p => p.id === saved);
      if (preset) this.applyAccent(preset);
      else this.clearAccent();
    } catch {
      // Ignore unavailable storage.
    }
  }

  /** Re-evaluates the accent when the user toggles light/dark or classic/new (attributes on <html>). */
  private watchThemeForAccent(): void {
    if (!this.showAccentPicker || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => this.syncAccent());
    observer.observe(this.doc.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-ui'] });
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected toggleAccount(): void {
    this.accountOpen.update(v => !v);
  }

  // Logout: same flow as the classic navbar (confirm dialog, clear storage, dispatch logout).
  protected logout(): void {
    this.accountOpen.set(false);
    this.showLogoutConfirm.set(true);
  }

  protected performLogout(): void {
    this.showLogoutConfirm.set(false);
    this.userDetailService._loggeedOnUserSubject.next(null);
    localStorage.clear();
    this.store.dispatch(AuthActions.logout({ authUser: undefined }));
    this.store.dispatch(ResetDashboardStateAction.resetDashBoardState());
  }

  protected openAiAssistant(): void {
    this.isAiAssistantOpen.set(true);
  }

  protected closeAiAssistant(): void {
    this.isAiAssistantOpen.set(false);
    // Return focus to the trigger (also covers browsers that do not focus a button on click).
    this.askAiBtn()?.nativeElement.focus();
  }

  protected navigateHomeFromApp(): void {
    const appNavigation = (window as AppWebViewWindow).AppNavigation;
    if (appNavigation?.postMessage) {
      appNavigation.postMessage('home');
      return;
    }

    window.history.back();
  }

  /** Falls back to the brand mark when the remote club logo fails (403, deleted object, DNS...). */
  protected onLogoError(): void {
    const failed = this.parentClubLogo();
    if (failed === FALLBACK_LOGO) return;
    console.warn('[new-shell] Club logo failed to load, falling back to brand mark. URL:', failed);
    this.parentClubLogo.set(FALLBACK_LOGO);
  }

  /**
   * Sessions that logged in before the club logo was stored in the `USER` blob have an empty
   * value. Read the club row (`ParentClubAppIconURL`), push it back through the shared stream
   * and persist it so later navigations do not refetch. Mirrors the classic navbar.
   */
  private resolveClubLogo(user: any): void {
    if (this.resolvingLogo) return;
    const parentClubId = this.ls.getPostgreParentClubId();
    if (!parentClubId) return;

    this.resolvingLogo = true;
    this.authService.getParentClubByPostgreId(parentClubId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (club: any) => {
          const logo = club?.ParentClubAppIconURL || club?.parentClubAppIconURL
            || club?.data?.ParentClubAppIconURL || club?.data?.parentClubAppIconURL || '';
          if (!logo) return;
          this.parentClubLogo.set(logo);
          const updated = { ...user, parentClubLogo: logo };
          localStorage.setItem('USER', JSON.stringify(updated));
          this.userDetailService.setUserDetails(updated);
        },
        error: () => { this.resolvingLogo = false; },
      });
  }

  private loadCollapsed(): boolean {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private mediaSignal(query: string) {
    const mql = window.matchMedia(query);
    const state = signal(mql.matches);
    const listener = (e: MediaQueryListEvent) => state.set(e.matches);
    mql.addEventListener('change', listener);
    this.destroyRef.onDestroy(() => mql.removeEventListener('change', listener));
    return state.asReadonly();
  }

  private detectAdminAppWebView(): boolean {
    if (localStorage.getItem('AP_ADMIN_APP_WEBVIEW') === 'true') return true;

    const userAgent = navigator.userAgent;
    const isAndroidWebView = /Android/i.test(userAgent) &&
      (/;\s*wv\)/i.test(userAgent) || /Version\/\d+(?:\.\d+)*\s+Chrome\//i.test(userAgent));
    const isIosDevice = /iPhone|iPad|iPod/i.test(userAgent);
    const isIosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const isIosWebView = isIosDevice && /AppleWebKit/i.test(userAgent) &&
      !/Safari\//i.test(userAgent) && !isIosStandalone;

    return isAndroidWebView || isIosWebView;
  }
}
